import { Tag } from "antd"
import { Layers } from "lucide-react"

import { usePmbSection } from "~/hooks/pre-meeting"
import { type PmbSectionCtx } from "~/api/pre-meeting-v2"
import { type TechStackPayload } from "~/types/pre-meeting"

import { SectionShell } from "../section-ui"

// Presentational view: renders from a payload directly. Shared by the drawer
// (fetches via usePmbSection) and the ASKOS agent widget (payload delivered in
// the agent stream). Self-hides when empty/loading/errored via SectionShell.
export function TechStackSectionView({
  payload,
  isLoading = false,
  error = null
}: {
  payload?: TechStackPayload | null
  isLoading?: boolean
  error?: string | null
}) {
  const top = payload?.top || []
  const isEmpty = !top.length

  return (
    <SectionShell
      id="tech_stack"
      styleKey="tech_stack"
      icon={<Layers size={16} />}
      title="Top Technologies Relevant for This Person at This Account"
      subtitle="Ranked by this person's likely focus area"
      isLoading={isLoading}
      error={error}
      isEmpty={isEmpty}
      emptyText="No tech stack on file for this account yet."
    >
      <div className="flex flex-wrap gap-2">
        {top.map((t) => (
          <Tag
            key={`${t.category}-${t.name}`}
            className="!px-3 !py-1 !rounded-full"
            title={t.relevance || undefined}
          >
            <span className="font-medium">{t.name}</span>
            <span className="text-gray-400 ml-1 text-[11px]">{t.category}</span>
          </Tag>
        ))}
      </div>
    </SectionShell>
  )
}

export function TechStackSection({
  ctx,
  enabled
}: {
  ctx: PmbSectionCtx
  enabled: boolean
}) {
  const { data, isLoading, isError } = usePmbSection<TechStackPayload>(
    "tech_stack",
    ctx,
    enabled
  )
  const error = data?.error || (isError ? "Could not load tech stack." : null)
  return (
    <TechStackSectionView
      payload={data?.payload}
      isLoading={isLoading}
      error={error}
    />
  )
}
